import { cn, formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetTraceabilityReport, useGetTraceabilityReportByGL } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { cardTemplates } from "./consts";
import moment from "moment";
import { Button } from "@/components/ui";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";

const useTraceabilityReport = () => {
  const {communityData, currentFinanceYear} = useSelector((state: RootState) => state.currentData);
  const communityName = communityData?.name || communityData?.communityName || "";
  const navigate = useNavigate();
  const [selectedGlId, setSelectedGlId] = useState<string>("");
  const [tableData, setTableData] = useState<any>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [openGroupGlDialog, setOpenGroupGlDialog] = useState<boolean>(false);
  const [openInfoDialog, setOpenInfoDialog] = useState<boolean>(false);
  const groupGlList = useRef<any[]>([]);
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: "",
        name: <span className="text-primary">Traceability Report</span>,
      },
    ],
    [communityName]
  );
  
  const {data: traceabilityReportData, isLoading: isLoadingReportData} = useGetTraceabilityReport({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })

  const {data: glReportData, isLoading: isLoadingGLReportData} = useGetTraceabilityReportByGL({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    glMasterId: selectedGlId,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear && selectedGlId)
    }
  })

  useEffect(() => {
    if (traceabilityReportData) {
      setTableData(traceabilityReportData?.gls || []);
    }
  }, [traceabilityReportData]);

  useEffect(() => {
    if (glReportData) {
      const _tableData = [...tableData];
      const idx = _tableData?.findIndex(
        (val: any) => val.reportingGlId == selectedGlId
      );
      
      const grouped = glReportData?.rows?.sort(
        (a: any, b: any) =>
          new Date(b.billedDate).getTime() - new Date(a.billedDate).getTime()
      )?.reduce((acc: any, row: any, rowIdx: number) => {
        (acc[row.jobId] = acc[row.jobId] || []).push({...row, caId: rowIdx, glMasterId: selectedGlId});
        return acc;
      }, {});

      const jobChildren = Object.entries(grouped).map(([jobId, rows]: [string, any]) => ({
        key: `job-${jobId}`,
        jobId: Number(jobId),
        isJobNode: true,
        ...rows[0],
        children: rows.map((row: any, rowIdx: number) => ({
          ...row,
          caId: rowIdx,
          key: `row-${row.caId || rowIdx}`,
        })),
      }));

      _tableData[idx] = {
        ..._tableData[idx],
        children: jobChildren,
      };

      setTableData([..._tableData]);
    }
  }, [glReportData]);

  // Inside your component:
  const summaryCards = useMemo(() => {
    // Ensure we are working with an array fallback safely
    const glList = Array.isArray(traceabilityReportData?.gls) ? traceabilityReportData.gls : [];
    
    // 1. Calculate totals by aggregating the array elements in a single pass
    const totals = glList.reduce(
      (acc: any, item: any) => {
        acc.totalOrigin += Number(item?.totalOrigin) || 0;
        acc.totalDistributed += Number(item?.totalDistributed) || 0;
        acc.sourceLines += Number(item?.sourceLines) || 0;
        acc.receiverLegs += Number(item?.receiverLegs) || 0;
        return acc;
      },
      { totalOrigin: 0, totalDistributed: 0, sourceLines: 0, receiverLegs: 0 }
    );
    
    // 2. Perform the mathematical checks
    const difference = totals.totalOrigin - totals.totalDistributed;
    const isBalanced = difference === 0;
  
    // 3. Map structural static template fields over our calculated aggregate totals
    const structuralCards = cardTemplates.map((card) => {
      // Dynamic key selection from our accumulated totals dictionary object
      const amtValue = totals[card.amtKey as keyof typeof totals] || 0;
      const descValue = totals[card.descKey as keyof typeof totals] || 0;
  
      return {
        ...card,
        id: card.id,
        title: card.title,
        amt: formatAmount(String(amtValue)),
        desc: `${descValue} ${card.suffix}`,
      };
    });
  
    // 4. Return the combined dataset
    return [
      ...structuralCards,
      {
        id: "diff",
        title: "Difference",
        amt: formatAmount(String(difference)),
        desc: "Origin - Distributed",
        isBalanced
      },
      {
        id: "integrity",
        title: "Integrity",
        text: isBalanced ? "Balanced" : "Unbalanced",
        desc: isBalanced ? "No value lost or double-counted" : "Discrepancy detected",
        isBalanced
      },
    ];
  }, [traceabilityReportData, cardTemplates]);

  const columns = [
    {
      accessorKey: "accountNumber",
      header: <div>GL No</div>,
      cell: ({row}: any) => <div className="flex items-center flex-row gap-0">
        <span>{row?.original?.accountNumber}</span>
        {row?.original?.groupGl ? 
          <Button className="p-0" variant="ghost" 
            onClick={(e: any) => {
              e.preventDefault();
              setOpenGroupGlDialog(true);
              groupGlList.current = row?.original?.memberGls;
            }}
          >
            <Info className="h-4 w-4 stroke-primary" />
          </Button> : <></>
        }
      </div>
    },
    {
      accessorKey: "receiverLegs",
      header: <div>No of Docs.</div>,
    },
    {
      accessorKey: "totalOrigin",
      header: <div>Total Origin Amount</div>,
      cell: (row: any) => {
        return row?.groupGlRow ? (
          <Button variant="link" 
            onClick={() => {
              setSelectedGlId(row?.glMasterId);
              setOpenInfoDialog(true);
            }}>{formatAmount(row?.originAmount || "0")}
          </Button>
        ) : (
          <span>{formatAmount(row?.originAmount || "0")}</span>
        )
      }
    },
    {
      accessorKey: "totalDistributed",
      header: <div>Total Distributed Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.totalDistributed || "0")
    },
    {
      accessorKey: "difference",
      header: <div>Difference</div>,
      cell: ({row}: any) => {
        const diff = Number(row.original?.totalOrigin) - Number(row.original?.totalDistributed);
        return <span className={cn(diff == 0 ? "text-green-600" : "text-red-600")}>{formatAmount(diff || "0")}</span>
      }
    },
    {
      accessorKey: "integrity",
      header: <div>Integrity</div>,
      cell: ({row}: any) => {
        const isBalanced = (Number(row.original?.totalOrigin) - Number(row.original?.totalDistributed)) == 0 

        if (isBalanced) return <span className="text-green-600">Balanced</span>
        else return <span className="text-red-600">Unbalanced</span>
      }
    },
    {
      accessorKey: "rule",
      header: <div>Rule</div>,
      cell: ({row}: any) => {
        const accNumber = row?.original?.groupGl ? 
          row?.original?.memberGls?.map((data: any) => data?.glAccountNumber) 
          : []
        return <Button
          variant="link"
          className="p-0"
          onClick={() => navigate(`${routes.distributionRuleConfiguration}/${row?.original?.accountNumber}`, {state: {members: accNumber?.join(",")}})}
        >Link</Button>
      }
    },
  ];

  const subColumns = [
    {
      accessorKey: "docNumber",
      header: <div>Post Doc. ID</div>,
      canHide: (row: any) => row?.groupGl,
    },
    {
      accessorKey: "originLevel",
      header: <div>Origin Level</div>,
    },
    {
      accessorKey: "originName",
      header: <div>Origin</div>,
    },
    {
      accessorKey: "originAmount",
      header: <div>Origin Amount</div>,
      cell: (row: any) => <span className="text-primary">{formatAmount(row?.originAmount || "0")}</span>
    },
    {
      accessorKey: "receiverLevel",
      header: <div>Receiver Level</div>,
    },
    {
      accessorKey: "distributionDate",
      header: <div>Allocation Date</div>,
      accessorFn: (row: any) => row?.distributionDate ? moment(row?.distributionDate).format('MM/DD/YYYY') : ''
    },
  ];

  const level2Columns = [
    {
      accessorKey: "caId",
      header: <div>CA ID</div>,
    },
    {
      accessorKey: "lineagePath",
      header: <div>Lineage Path</div>,
    },
    {
      accessorKey: "receiverName",
      header: <div>Receiver</div>,
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Receiver Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.amount || "0")
    },
    {
      accessorKey: "sharePct",
      header: <div>% of Origin</div>,
      accessorFn: (row: any) => row?.sharePct ? `${row?.sharePct}%` : ''
    },
  ];

  const onRowExpanded = (row: any) => {
    setSelectedGlId(row?.original?.reportingGlId);
  };

  return {
    breadcrumbData,
    isLoadingReportData,
    summaryCards,
    columns,
    subColumns,
    onRowExpanded,
    isLoadingGLReportData,
    tableData,
    globalFilter,
    setGlobalFilter,
    currentFinanceYear,
    level2Columns,
    setOpenGroupGlDialog,
    openGroupGlDialog,
    groupGlList: groupGlList.current,
    openInfoDialog,
    setOpenInfoDialog,
    selectedGlId
  }
}

export default useTraceabilityReport;
